import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../styles/client/ClientQuoteView.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientQuoteView = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [modalitePaiement, setModalitePaiement] = useState('100_avant');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchQuote();
  }, [token]);

  const fetchQuote = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client-portal/quote/${token}`);
      setQuote(response.data);
      // Sélectionner tous les services par défaut
      setSelectedServices(response.data.items.map(item => item.service_id));
      setLoading(false);
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.response?.data?.detail || 'Impossible de charger le devis');
      setLoading(false);
    }
  };

  const toggleService = (serviceId) => {
    if (selectedServices.includes(serviceId)) {
      setSelectedServices(selectedServices.filter(id => id !== serviceId));
    } else {
      setSelectedServices([...selectedServices, serviceId]);
    }
  };

  const calculateTotal = () => {
    if (!quote) return { subtotal: 0, tps: 0, tvq: 0, total: 0 };

    const subtotal = quote.items
      .filter(item => selectedServices.includes(item.service_id))
      .reduce((sum, item) => sum + (item.sous_total || item.prix_final || 0), 0);

    const tps = subtotal * 0.05;
    const tvq = subtotal * 0.09975;
    const total = subtotal + tps + tvq;

    return { subtotal, tps, tvq, total };
  };

  const handleAccept = () => {
    if (selectedServices.length === 0) {
      alert('Veuillez sélectionner au moins un service');
      return;
    }
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      alert('Les mots de passe ne correspondent pas');
      return;
    }

    if (password.length < 8) {
      alert('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/client-portal/quote/${token}/accept`, {
        services_selected: selectedServices,
        modalite_paiement: modalitePaiement,
        password: password,
        confirm_password: confirmPassword
      });

      navigate(`/client/confirmation?quote_id=${quote.id}`);
    } catch (err) {
      console.error('Erreur acceptation:', err);
      alert(err.response?.data?.detail || 'Erreur lors de l\'acceptation');
      setSubmitting(false);
    }
  };

  const handleRefuse = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir refuser ce devis?')) return;

    try {
      await axios.post(`${API_URL}/api/client-portal/quote/${token}/refuse`, {
        raison: ''
      });
      alert('Votre réponse a été enregistrée. Nous vous contacterons prochainement.');
    } catch (err) {
      console.error('Erreur refus:', err);
      alert('Erreur lors du refus');
    }
  };

  const handleContact = async () => {
    try {
      await axios.post(`${API_URL}/api/client-portal/quote/${token}/contact`, {
        message: ''
      });
      alert('Nous vous contacterons sous peu!');
    } catch (err) {
      console.error('Erreur contact:', err);
      alert('Erreur lors de la demande');
    }
  };

  if (loading) {
    return (
      <div className="client-quote-loading">
        <div className="loading-spinner"></div>
        <p>Chargement de votre devis...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="client-quote-error">
        <h2>⚠️ Erreur</h2>
        <p>{error}</p>
      </div>
    );
  }

  const totals = calculateTotal();

  return (
    <div className="client-quote-view">
      <header className="quote-header">
        <div className="header-content">
          <h1>🌿 Naturellement Vert Fertilisation</h1>
          <p className="header-subtitle">Votre devis personnalisé</p>
        </div>
      </header>

      <div className="quote-container">
        <div className="quote-info-card">
          <h2>Devis #{quote.numero_devis}</h2>
          <div className="client-info">
            <p><strong>Client:</strong> {quote.client_nom}</p>
            <p><strong>Adresse:</strong> {quote.client_adresse}</p>
            <p><strong>Date:</strong> {new Date(quote.date_creation).toLocaleDateString('fr-CA')}</p>
          </div>
        </div>

        <div className="services-section">
          <h3>📋 Services proposés</h3>
          <p className="services-instruction">Sélectionnez les services que vous souhaitez:</p>
          
          <div className="services-list">
            {quote.items.map((item) => (
              <div 
                key={item.service_id}
                className={`service-item ${selectedServices.includes(item.service_id) ? 'selected' : ''}`}
                onClick={() => toggleService(item.service_id)}
              >
                <div className="service-checkbox">
                  <input 
                    type="checkbox" 
                    checked={selectedServices.includes(item.service_id)}
                    readOnly
                  />
                </div>
                <div className="service-details">
                  <h4>{item.service_nom}</h4>
                  <p>{item.description}</p>
                  <div className="service-pricing">
                    <span>{item.quantite} {item.unite} × {(item.prix_unitaire || 0).toFixed(3)}$</span>
                  </div>
                </div>
                <div className="service-price">
                  {(item.sous_total || item.prix_final || 0).toFixed(2)}$
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="payment-section">
          <h3>💳 Modalité de paiement</h3>
          <div className="payment-options">
            <label className={`payment-option ${modalitePaiement === '100_avant' ? 'selected' : ''}`}>
              <input 
                type="radio" 
                value="100_avant"
                checked={modalitePaiement === '100_avant'}
                onChange={(e) => setModalitePaiement(e.target.value)}
              />
              <span>100% avant les travaux</span>
            </label>
            <label className={`payment-option ${modalitePaiement === '60_40' ? 'selected' : ''}`}>
              <input 
                type="radio" 
                value="60_40"
                checked={modalitePaiement === '60_40'}
                onChange={(e) => setModalitePaiement(e.target.value)}
              />
              <span>60% avant / 40% après</span>
            </label>
          </div>
        </div>

        <div className="totals-section">
          <div className="total-row">
            <span>Sous-total:</span>
            <span>{totals.subtotal.toFixed(2)}$</span>
          </div>
          <div className="total-row">
            <span>TPS (5%):</span>
            <span>{totals.tps.toFixed(2)}$</span>
          </div>
          <div className="total-row">
            <span>TVQ (9.975%):</span>
            <span>{totals.tvq.toFixed(2)}$</span>
          </div>
          <div className="total-row grand-total">
            <span>Total:</span>
            <span>{totals.total.toFixed(2)}$</span>
          </div>
        </div>

        <div className="actions-section">
          <button className="btn-accept" onClick={handleAccept}>
            ✅ Accepter ce devis
          </button>
          <button className="btn-refuse" onClick={handleRefuse}>
            ❌ Refuser
          </button>
          <button className="btn-contact" onClick={handleContact}>
            📞 Me contacter
          </button>
        </div>
      </div>

      {showPasswordModal && (
        <div className="password-modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="password-modal" onClick={(e) => e.stopPropagation()}>
            <h3>🔐 Créez votre compte</h3>
            <p>Pour accepter ce devis, créez un mot de passe sécurisé:</p>
            
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group">
                <label>Email:</label>
                <input type="email" value={quote.client_nom} disabled />
              </div>
              
              <div className="form-group">
                <label>Mot de passe: *</label>
                <input 
                  type="password" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 caractères"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Confirmer le mot de passe: *</label>
                <input 
                  type="password" 
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirmez votre mot de passe"
                  required
                />
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={() => setShowPasswordModal(false)}
                  disabled={submitting}
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  className="btn-submit"
                  disabled={submitting}
                >
                  {submitting ? 'Traitement...' : 'Confirmer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientQuoteView;
